import { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useStripeContext } from '../../../contexts/StripeContext';
import { subscriptionService } from '../../../services/subscriptionService';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CheckoutForm = ({ plan, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);

  useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/account-settings?tab=billing`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setErrorMessage(stripeError?.message || 'Payment failed. Please try again.');
      } else if (paymentIntent && paymentIntent?.status === 'succeeded') {
        // Confirm subscription on backend
        await subscriptionService?.confirmSubscription(
          paymentIntent?.id,
          plan?.id,
          user?.id
        );
        
        onSuccess?.();
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrorMessage(error?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stripe || !elements || !clientSecret) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Loading payment system...
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-card-foreground">
            Payment Details
          </label>
          <PaymentElement 
            options={{
              fields: {
                billingDetails: 'auto'
              },
              layout: 'tabs'
            }}
          />
        </div>
      </div>
      {errorMessage && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-destructive text-sm">{errorMessage}</p>
        </div>
      )}
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!paymentReady || isProcessing || !stripe || !elements}
          fullWidth
          iconName={isProcessing ? "Loader2" : "CreditCard"}
          iconClassName={isProcessing ? "animate-spin" : ""}
          iconPosition="left"
        >
          {isProcessing ? 'Processing...' : `Pay ${subscriptionService?.formatCurrency(plan?.base_price)}`}
        </Button>
      </div>
      <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
        <Icon name="Shield" size={14} />
        <span>Secured by Stripe • Your payment information is encrypted</span>
      </div>
    </form>
  );
};

const SubscriptionCheckout = ({ plan, isOpen, onClose, onSuccess }) => {
  const { stripePromise, stripeOptions } = useStripeContext();
  const { user } = useAuth();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && plan && user) {
      initializePayment();
    }
  }, [isOpen, plan, user]);

  const initializePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await subscriptionService?.createPaymentIntent(
        plan?.id,
        user?.id,
        {
          email: user?.email,
          name: user?.user_metadata?.full_name
        }
      );

      setClientSecret(result?.clientSecret);
    } catch (err) {
      console.error('Failed to initialize payment:', err);
      setError(err?.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    onSuccess?.();
    onClose?.();
  };

  if (!isOpen) return null;

  const elementsOptions = {
    clientSecret,
    ...stripeOptions
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Subscribe to {plan?.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Complete your payment to activate your subscription
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Plan Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Plan</span>
              <span className="text-sm text-foreground">{plan?.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Billing</span>
              <span className="text-sm text-foreground">
                {plan?.billing_interval === 'year' ? 'Yearly' : 'Monthly'}
              </span>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <span className="text-base font-semibold text-foreground">Total</span>
              <span className="text-2xl font-bold text-primary">
                {subscriptionService?.formatCurrency(plan?.base_price)}
              </span>
            </div>
          </div>

          {/* Payment Form */}
          {loading && (
            <div className="text-center py-8">
              <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Initializing payment...</p>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <p className="text-destructive text-sm">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={initializePayment}
                className="mt-3"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retry
              </Button>
            </div>
          )}

          {!loading && !error && clientSecret && (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CheckoutForm
                plan={plan}
                clientSecret={clientSecret}
                onSuccess={handleSuccess}
                onCancel={onClose}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckout;